import helpers from './helpers';
import elements from './elements';

const defaultOptions = {
    items: [],
    icons: [],
    signal: null,
    selected: [],
    chunkedItems: [],
    groupedItems: [],
    alreadyOffsets: [],
    hasMore: true,
    loading: true,
    grouped: false,
    fetching: false,
    controller: null,
    prefetchResponse: {},
    showEmptyState: false,

    label: null,
    labelHeight: 30,
    labelQuantity: 0,
    fragment: null,

    columns: 0,
    totalRows: null,
    requestId: 0,
    lastRepaintY: 0,
    lastHoleOffset: null,
    favoriteRows: 0,
    cachedItemsLen: 0,
    screenItemsLen: 0,
    totalRowsCount: 0,

    settings: {
        amount: 5,
        totalRows: 0,
        totalHeight: 0,
        itemHeight: [260, 200],
        maxWidth: 175,
        minColumns: 1,
        itemWidth: 186,
        gap: 5,
    },

    ...helpers,
    ...elements,
};

export default (route, images = [], settings = {}) => ({
    ...defaultOptions,

    options: {},
    scroller: null,
    container: null,
    filterByTeamId: null,
    lastTimestamp: null,
    lastScrollTop: 0,

    init() {
        this.$watch('preview', () => this.observerPreview());
    },

    prepare() {
        this.items = images ?? [];
        this.alreadyOffsets = [];

        this.preview = this.$refs.preview;
        this.container = this.$refs.container;
        this.scroller = this.$el.querySelector('[x-ref="scroller"]');
        this.fetching = _.get(this.options, 'fetching', false);

        this.movePreviewToOutside();

        this.settings = { ...this.settings, ...settings };
        this.settings.amount = _.get(this.options, 'amount', this.settings.amount);

        if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
            this.$el.classList.add('ios');
        }

        window.addEventListener('filter-team', ({ detail }) => {
            this.filterByTeamId = typeof detail == 'number' ? detail : null;

            this.render();
        });

        this.start();
        this.addResizeEvent();
    },

    cancelPendingRequest() {
        this.controller && this.controller.abort();
    },

    addResizeEvent() {
        this.$nextTick(() => {
            window.addEventListener('resize', () => {
                const resizeObserver = new ResizeObserver((_) => {
                    this.debounce(this.render(false), 400);
                });

                resizeObserver.observe(this.container);
            });
        });
    },

    refresh({ detail }) {
        this.setOptions({ detail });

        if (_.has(detail, 'images')) {
            this.items = detail.images;

            this.removeLabels();
            this.prepareItemsWithGroup();
        }

        if (_.has(detail, 'remove')) {
            const remove = detail.remove;
            this.groupedItems = this.groupedItems.filter((item) => !remove.includes(item.id));
        }

        this.checkLoading(false);
        this.selected = _.get(detail, 'selected', []);

        if (_.get(detail, 'image')) {
            const imageId = _.get(detail.image, 'id');

            const groupImageIndex = _.findIndex(this.groupedItems, { id: imageId });
            this.renderButtons(this.groupedItems[groupImageIndex], groupImageIndex);

            if (_.get(detail, 'save') == true) {
                this.images[_.findIndex(this.images, { id: imageId })] = _.get(detail, 'image');
            } else {
                const imageIndex = _.findIndex(this.items, { id: imageId });
                this.items[imageIndex].favorite = detail.image.favorite;
                this.items[imageIndex].saved = detail.image.saved;
            }
        } else {
            this.render();
        }

        if (_.has(detail, 'allSelected')) {
            this.selectAll(_.get(detail, 'allSelected'));
        }
    },

    selectAll(saved = true) {
        _.each(this.items, (image) => {
            image.saved = saved;
            if (!saved) image.favorite = saved;
        });
    },

    destroy() {
        this.restart({ detail: {} });
    },

    restart({ detail }) {
        this.setOptions({ detail });

        this.showEmptyState = false;
        this.alreadyOffsets = [];
        this.preloaded = [];
        this.totalRows = null;
        this.lastHoleOffset = null;
        this.hasMore = true;

        this.addLoadingGrid();

        this.items = _.get(detail, 'images', []);
        this.prepareItemsWithGroup();

        if (_.has(detail, 'params')) {
            this.options.params = _.omitBy(
                {
                    ...this.options.params,
                    ...detail.params,
                },
                _.isNil,
            );
        }

        if (this.fetching && !this.controller.signal.aborted) {
            this.controller.abort();
        }

        this.fetching = _.get(detail, 'fetching', false);
        this.container.scrollTop = 0;
        this.start();
    },

    async start() {
        this.addLoadingGrid();
        this.loadImages();
    },

    scrollToIndex({ detail }) {
        const index = _.get(detail, 'index', null);

        if (index === null || !this.container || this.loading) {
            return;
        }

        const itemHeight = this.getHeight();
        const columns = Math.max(1, this.columns);
        const total = this.totalRows ?? this.getCurrentItems().length;

        const clamped = Math.max(0, Math.min(index, Math.max(0, total - 1)));
        const row = Math.floor(clamped / columns);

        let offset = row * itemHeight + this.settings.gap;

        if (this.grouped) {
            offset += this.getLabelsOffset(clamped);
        }

        const maxScroll = Math.max(0, this.scroller.offsetHeight - this.container.clientHeight);

        this.container.scrollTop = Math.min(offset, maxScroll);
        this.lastRepaintY = this.container.scrollTop;

        this.hasMore ? this.loadImages() : this.render();
    },

    addLoadingGrid() {
        const maxWidth = this.getMaxWidth();
        const itemHeight = this.getHeight();

        this.$nextTick(() => {
            const containerWidth = this.$el.clientWidth;
            const containerHeight = this.$el.clientHeight;

            this.columns = Math.floor(containerWidth / maxWidth);
            this.columns = Math.max(this.settings.minColumns, this.columns);
            let rows = Math.floor(containerHeight / itemHeight);

            if (!Number.isInteger(this.columns / rows)) {
                rows = Math.max(1, rows - 1);
            }

            const fragment = document.createDocumentFragment();
            const container = this.createColumn(1);

            for (let i = 0; i < this.columns * rows; i++) {
                const column = document.createElement('div');
                column.style.height = `${itemHeight}px`;
                column.classList.add('bg-gray-200', 'rounded');
                fragment.appendChild(column);
            }

            container.appendChild(fragment);

            this.container.replaceChildren(this.scroller, container);

            const heightOption = this.getContainerHeight(
                `${containerHeight - this.settings.gap}px`,
            );

            if (this.hasDynamicHeight(heightOption)) {
                this.applyMaxHeight();
            } else {
                this.container.style.height = heightOption;
            }

            this.container.classList.add('overflow-hidden');

            this.checkIfFetching();
        });
    },

    loadImages(offset = null) {
        const params = this.getLoadImagesParams();

        if (offset) {
            params.offset = offset;
        }

        if (!route && !this.fetching) {
            this.prepareItemsWithGroup();

            this.hasMore = false;
            this.loading = false;

            this.render();

            return;
        }

        if (this.fetching || !this.hasMore) {
            return;
        }

        this.fetching = true;
        const requestId = ++this.requestId;

        this.fetchImages(params)
            .catch((error) => {
                if (error.name !== 'AbortError') {
                    console.error(error);
                }
            })
            .then(() => {
                if (requestId !== this.requestId) {
                    return;
                }

                this.fetching = false;
                this.loading = false;

                this.fillPendingImages();
            });
    },

    fillPendingImages() {
        if (this.fetching || !this.hasMore || !this.container) {
            return;
        }

        const itemHeight = this.getHeight();
        const columns = Math.max(this.columns, 1);

        const first = Math.floor(this.container.scrollTop / itemHeight) * columns;
        const last = first + Math.max(this.screenItemsLen, 1) * columns;

        const holeIndex = _.findIndex(
            this.items.slice(Math.max(0, first), last),
            (item) => !item || item.id === undefined,
        );

        if (holeIndex === -1) {
            return;
        }

        const offset = Math.max(0, first) + holeIndex;

        if (this.lastHoleOffset === offset) {
            return;
        }

        this.lastHoleOffset = offset;
        this.loadImages(offset);
    },

    fetchImages(params, needRender = true) {
        if (this.fetching) {
            this.cancelPendingRequest();
        }

        this.controller = new AbortController();
        this.signal = this.controller.signal;

        if (!this.alreadyOffsets.includes(params.offset)) {
            this.alreadyOffsets.push(params.offset);
        }

        return fetch(route + '?' + new URLSearchParams(params), { signal: this.signal })
            .then((response) => response.json())
            .then(async (response) => {
                if (_.has(response, 'total')) {
                    const total = _.get(response, 'total');

                    if (total !== null) {
                        this.totalRowsCount = total;
                        this.prefetchResponse = response;
                        this.favoriteRows = _.get(response, 'favorite_count', 0);
                        this.totalRows = _.get(response, 'total_filtered', total);

                        let emptyData = {};
                        const groupKey = this.options.groupBy ?? 'group';

                        emptyData[groupKey] = _.get(response.data[0], groupKey, 'Loading...');

                        const emptyItems = new Array(this.totalRows).fill(emptyData);
                        this.items = emptyItems;
                    }

                    if (this.totalRows == 0) {
                        this.showEmptyState = true;
                        this.dispatchRenderedEvent();
                    }
                }

                if (!response.data.length) {
                    this.hasMore = false;
                    this.dispatchEvent('loaded', this.items);
                    return;
                }

                await response.data.forEach((image, index) => {
                    const position = index + params.offset;
                    this.items[position] = image;
                });

                this.prepareItemsWithGroup(false);
                needRender && this.render();

                if (_.has(response, 'hasMore')) {
                    this.hasMore = _.get(response, 'hasMore');
                }

                this.dispatchEvent('loaded', this.items);
                this.$dispatch('update-request-data', {
                    offsets: this.alreadyOffsets,
                    limit: params.limit,
                });
            });
    },

    getLoadImagesParams() {
        if (this.cachedItemsLen === 0) {
            this.calcColumnsAndRows(true);
        }

        let limit = Math.max(this.screenItemsLen * this.columns * 3, 50);

        let scrollPercent = (this.container?.scrollTop * 100) / this.container?.scrollHeight;
        scrollPercent = scrollPercent > 99 ? 100 : scrollPercent;

        const currentPage = this.totalRowsCount * (scrollPercent / 100);
        let offset = Math.round(currentPage - limit / 2);
        offset = Math.max(
            offset,
            _.findIndex(this.items, ({ id }) => id === undefined),
        );

        const params = _.get(this.options, 'params', {});

        limit = this.items.length ? limit : limit / 3;
        limit = this.totalRowsCount ? Math.min(this.totalRowsCount, limit) : limit;

        return {
            ..._.omitBy(params, _.isNil),
            offset: Math.max(isNaN(offset) ? 0 : offset, 0),
            limit: Math.floor(limit),
        };
    },

    render(checkEmpty = true) {
        const currentItems = this.getCurrentItems();

        if (checkEmpty) {
            this.showEmptyState = !currentItems.length;
        }

        if (this.loading && !this.totalRows) {
            this.addLoadingGrid();
            return;
        }

        this.$nextTick(() => {
            const maxWidth = this.getMaxWidth();
            const itemHeight = this.getHeight();
            const container = this.container;
            const bottomPadding = this.getBottomPadding();

            if (!container) {
                return;
            }

            this.calcColumnsAndRows();
            this.settings.totalRows = Math.ceil(
                (this.totalRows ?? currentItems.length) / this.columns,
            );

            this.settings.totalHeight = this.settings.totalRows * itemHeight;
            this.settings.totalHeight += bottomPadding;

            this.scroller.style.height = `${this.settings.totalHeight}px`;

            let containerHeight = this.getAmount() * itemHeight;

            containerHeight = Math.min(containerHeight, this.settings.totalHeight);

            containerHeight += this.settings.gap * (this.getAmount() + 1) + 'px';
            const containerHeightOption = this.getContainerHeight(containerHeight);

            if (this.hasDynamicHeight(containerHeightOption)) {
                this.applyMaxHeight();
            } else {
                container.style.height = containerHeightOption;
            }

            this.container.classList.remove('overflow-hidden');

            this.settings.itemWidth = Math.min(
                Math.floor(this.$el.clientWidth / this.columns - this.settings.gap / 2),
                maxWidth,
            );

            this.chunkedItems = this.spliceIntoChunks(currentItems, Math.max(this.columns, 1));

            var first = parseInt(container.scrollTop / itemHeight) - this.screenItemsLen;
            first = Math.max(0, first);

            if (this.hasDynamicHeight(containerHeightOption)) {
                this.resizeContainer();
            }

            this._renderChunk(first, this.cachedItemsLen);
        });
    },

    resizeContainer() {
        this.setContainerHeight();
    },

    setContainerHeight() {
        setTimeout(() => this.applyMaxHeight(), 100);
    },

    applyMaxHeight() {
        const container = this.container;

        if (!container) {
            return;
        }

        const max = window.innerHeight - container.offsetTop + 'px';
        const available =
            Math.max(0, window.innerHeight - container.getBoundingClientRect().top) + 'px';

        const height = this.getContainerHeight()
            .replace('available', available)
            .replace('max', max);

        container.style.height = height;
        container.style.maxHeight = height;
    },

    renderButtons(imageObject, index) {
        const { element = null } = imageObject ?? {};

        this.checkIfInactive(imageObject);

        if (!element) {
            return;
        }

        element.querySelector('.bottom-buttons')?.remove();
        element.querySelector('.top-buttons')?.remove();

        element.appendChild(this.createTopButtons(imageObject, element, index));
        element.appendChild(this.createBottomButtons(imageObject, index));
    },

    dispatchEvent(eventName, data = {}) {
        const event = new CustomEvent(eventName, { detail: data });
        this.$el.dispatchEvent(event);
    },

    _renderChunk(fromPos, howMany) {
        const { totalRows, gap } = this.settings;
        const itemHeight = this.getHeight();
        const node = this.container;

        if (!node) return;

        let finalItem = Math.min(fromPos + howMany, totalRows);
        let element = null;

        this.fragment = document.createDocumentFragment();
        this.label = null;

        this.$nextTick(() => {
            !this.fetching && this.preloadImages(fromPos, finalItem);
        });

        for (let chunkIndex = fromPos; chunkIndex < finalItem; chunkIndex++) {
            const { fragment, element: newElement } = this.createImage(chunkIndex, element);
            element = newElement;
            this.fragment.appendChild(fragment);
        }

        this.fragment.querySelectorAll('.vrow:not(.positioned)').forEach((item, index) => {
            const currentIndex = fromPos + index;
            let columnHeight = currentIndex * itemHeight;
            let labelHeight = 0;
            let prevElement = item.previousSibling;

            item.classList.add('positioned');
            item.setAttribute('data-clarity-mask', 'true');
            item.style.position = 'absolute';

            if (prevElement?.classList.contains('label')) {
                const elementLabel = prevElement.cloneNode(true);
                elementLabel.classList.toggle('px-3');

                document.body.appendChild(elementLabel);
                labelHeight = elementLabel.firstChild.offsetHeight;
                elementLabel.remove();

                prevElement = prevElement.previousSibling;
            }

            if (prevElement?.classList.contains('vrow')) {
                columnHeight = parseInt(prevElement.style.top, 10) + itemHeight;
            }

            item.style.top = `${columnHeight + gap + labelHeight}px`;
        });

        node.replaceChildren(this.$refs.scroller, this.fragment);

        this.$nextTick(() => {
            this.moveLabels(node);
            this.dispatchRenderedEvent();
        });

        this.checkIfFetching();
    },

    debounce(func, wait) {
        let timeout;
        return function () {
            clearTimeout(timeout);
            timeout = setTimeout(() => func.apply(this), wait);
        };
    },

    runScroller(event) {
        this.debounce(this.onScrollEnd(event), 200);
    },

    onScrollEnd({ target, ...event }) {
        if (this.loading) {
            return;
        }

        const scrollTop = target.scrollTop;

        this.lastHoleOffset = null;

        const labelHeight = this.getLabelHeight();
        const itemHeight = this.getHeight() - labelHeight;
        const maxBuffer = this.screenItemsLen * itemHeight;
        const loadMore = _.get(this.options, 'loadMore');

        event.preventDefault && event.preventDefault();

        if (typeof loadMore === 'function') {
            loadMore(this, {
                scrollTop,
                maxBuffer,
                target,
            });

            return;
        }

        if (!this.lastRepaintY || Math.abs(scrollTop - this.lastRepaintY) > maxBuffer) {
            this.lastRepaintY = scrollTop;

            this.hasMore ? this.loadImages() : this.render();
        }
    },
});
